<template>
  <header class="site-header" :class="{ 'site-header--scrolled': isScrolled }">
    <div class="site-header__inner">
      <RouterLink
        to="/"
        class="brand"
        aria-label="Krilin Schmid – Startseite"
        @click="closeMenu"
      >
        <span class="brand__logo-frame">
          <img
            :src="headerLogo"
            alt="Krilin Schmid"
            class="brand__logo"
            decoding="async"
          >
        </span>
      </RouterLink>

      <nav class="desktop-nav" aria-label="Hauptnavigation">
        <a
          v-for="item in navItems"
          :key="item.id"
          href="#"
          class="desktop-nav__link"
          @click.prevent="scrollToSection(item.id)"
        >
          <span>{{ item.nr }}</span>
          {{ item.label }}
        </a>
      </nav>

      <a
        href="#"
        class="header-cta"
        @click.prevent="scrollToSection('contact')"
      >
        Anfrage starten
      </a>

      <button
        class="menu-button"
        type="button"
        :aria-expanded="menuOpen"
        aria-label="Navigation öffnen"
        @click="menuOpen = !menuOpen"
      >
        <span :class="{ active: menuOpen }"></span>
        <span :class="{ active: menuOpen }"></span>
      </button>
    </div>

    <transition name="mobile-menu">
      <nav v-if="menuOpen" class="mobile-nav" aria-label="Mobile Navigation">
        <a
          v-for="item in navItems"
          :key="item.id"
          href="#"
          class="mobile-nav__link"
          @click.prevent="handleMobileClick(item.id)"
        >
          <span>{{ item.nr }}</span>
          {{ item.label }}
        </a>

        <a
          href="#"
          class="mobile-nav__cta"
          @click.prevent="handleMobileClick('contact')"
        >
          Anfrage starten
        </a>
      </nav>
    </transition>
  </header>
</template>

<script setup>
import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import headerLogo from '@/assets/images/logos/krilin/KS_Logo_weiss.png'

const router = useRouter()
const route = useRoute()

const menuOpen = ref(false)
const isScrolled = ref(false)

const navItems = [
  { nr: '01', label: 'Services', id: 'services' },
  { nr: '02', label: 'About', id: 'about' },
  { nr: '03', label: 'Work', id: 'work' },
  { nr: '04', label: 'Kontakt', id: 'contact' },
]

function closeMenu() {
  menuOpen.value = false
}

function handleScroll() {
  isScrolled.value = window.scrollY > 16
}

async function scrollToSection(id) {
  closeMenu()

  if (route.path !== '/') {
    await router.push('/')
    await nextTick()
  }

  setTimeout(() => {
    const target = document.getElementById(id)

    if (!target) return

    const headerOffset = 100
    const targetPosition =
      target.getBoundingClientRect().top + window.scrollY - headerOffset

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    })
  }, 80)
}

function handleMobileClick(id) {
  scrollToSection(id)
}

onMounted(() => {
  handleScroll()
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
/* LOGO-BLAU: #373D6B · RGB 55, 61, 107 */
.site-header {
  --navy: #171a31;
  --navy-soft: #242a4c;
  --blue: #373d6b;
  --blue-light: #626c9f;
  --text: #ffffff;
  --text-soft: #cbd9ed;

  position: fixed;
  inset: 0 0 auto;
  z-index: 100;
  border-bottom: 1px solid rgba(98, 108, 159, 0.13);
  background:
    radial-gradient(circle at 18% 0%, rgba(55, 61, 107, 0.16), transparent 30%),
    rgba(23, 26, 49, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition:
    background 220ms ease,
    border-color 220ms ease,
    box-shadow 220ms ease;
}

.site-header--scrolled {
  border-color: rgba(98, 108, 159, 0.22);
  background:
    radial-gradient(circle at 18% 0%, rgba(55, 61, 107, 0.12), transparent 32%),
    rgba(23, 26, 49, 0.98);
  box-shadow: 0 18px 52px rgba(23, 26, 49, 0.24);
}

.site-header__inner {
  width: min(100% - 40px, 1240px);
  min-height: 84px;
  margin-inline: auto;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: 28px;
}

.brand {
  display: inline-flex;
  align-items: center;
  color: var(--text);
  text-decoration: none;
}

.brand__logo-frame {
  display: block;
  width: 152px;
  height: 54px;
  overflow: hidden;
}

.brand__logo {
  display: block;
  width: 200px;
  max-width: none;
  height: auto;
  transform: translate(-28px, -17px);
  filter: drop-shadow(0 9px 22px rgba(55, 61, 107, 0.14));
  transition:
    filter 200ms ease,
    opacity 200ms ease;
}

.brand:hover .brand__logo {
  opacity: 0.92;
  filter: drop-shadow(0 12px 28px rgba(98, 108, 159, 0.28));
}

.desktop-nav {
  justify-self: center;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid rgba(98, 108, 159, 0.13);
  border-radius: 999px;
  background: rgba(36, 42, 76, 0.7);
  padding: 5px;
}

.desktop-nav__link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  color: rgba(221, 232, 248, 0.78);
  font-size: 0.85rem;
  font-weight: 800;
  line-height: 1;
  padding: 12px 15px;
  text-decoration: none;
  transition:
    background 180ms ease,
    color 180ms ease,
    transform 180ms ease;
}

.desktop-nav__link span {
  color: #aeb3cf;
  font-size: 0.74rem;
}

.desktop-nav__link:hover {
  background: rgba(55, 61, 107, 0.15);
  color: #ffffff;
  transform: translateY(-1px);
}

.header-cta,
.mobile-nav__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  border: 1px solid rgba(98, 108, 159, 0.34);
  border-radius: 999px;
  background: linear-gradient(135deg, #626c9f, var(--blue));
  color: #ffffff;
  box-shadow:
    0 12px 29px rgba(55, 61, 107, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  font-size: 0.9rem;
  font-weight: 900;
  padding: 0 20px;
  text-decoration: none;
  transition:
    transform 180ms ease,
    box-shadow 180ms ease,
    background 180ms ease;
}

.header-cta:hover,
.mobile-nav__cta:hover {
  transform: translateY(-2px);
  background: linear-gradient(135deg, #737cab, #373d6b);
  box-shadow:
    0 16px 36px rgba(55, 61, 107, 0.34),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.brand:focus-visible,
.desktop-nav__link:focus-visible,
.header-cta:focus-visible,
.menu-button:focus-visible,
.mobile-nav__link:focus-visible,
.mobile-nav__cta:focus-visible {
  outline: 3px solid rgba(98, 108, 159, 0.48);
  outline-offset: 3px;
}

.menu-button {
  display: none;
  width: 46px;
  height: 46px;
  border: 1px solid rgba(98, 108, 159, 0.2);
  border-radius: 15px;
  background: rgba(36, 42, 76, 0.78);
  cursor: pointer;
  position: relative;
}

.menu-button span {
  position: absolute;
  left: 12px;
  right: 12px;
  height: 2px;
  border-radius: 99px;
  background: #ffffff;
  transition:
    transform 220ms ease,
    top 220ms ease;
}

.menu-button span:first-child {
  top: 17px;
}

.menu-button span:last-child {
  top: 27px;
}

.menu-button span:first-child.active {
  top: 22px;
  transform: rotate(45deg);
}

.menu-button span:last-child.active {
  top: 22px;
  transform: rotate(-45deg);
}

.mobile-nav {
  width: min(100% - 40px, 1240px);
  margin: 0 auto 18px;
  border: 1px solid rgba(98, 108, 159, 0.2);
  border-radius: 22px;
  background:
    radial-gradient(circle at 10% 0%, rgba(55, 61, 107, 0.15), transparent 34%),
    rgba(23, 26, 49, 0.98);
  padding: 10px;
  box-shadow: 0 24px 70px rgba(0, 7, 20, 0.38);
  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);
}

.mobile-nav__link {
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 15px;
  color: var(--text-soft);
  font-size: 1rem;
  font-weight: 800;
  padding: 16px;
  text-decoration: none;
}

.mobile-nav__link span {
  color: #aeb3cf;
  font-size: 0.82rem;
}

.mobile-nav__link:hover {
  background: rgba(55, 61, 107, 0.14);
  color: #ffffff;
}

.mobile-nav__cta {
  width: 100%;
  margin-top: 8px;
}

.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition:
    opacity 220ms ease,
    transform 220ms ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 980px) {
  .site-header__inner {
    grid-template-columns: auto 1fr auto;
  }

  .desktop-nav,
  .header-cta {
    display: none;
  }

  .menu-button {
    display: inline-flex;
    justify-self: end;
  }
}

@media (max-width: 520px) {
  .site-header__inner {
    width: min(100% - 28px, 1240px);
    min-height: 76px;
  }

  .mobile-nav {
    width: min(100% - 28px, 1240px);
  }

  .brand__logo-frame {
    width: 128px;
    height: 46px;
  }

  .brand__logo {
    width: 171px;
    transform: translate(-24px, -15px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .site-header,
  .brand__logo,
  .desktop-nav__link,
  .header-cta,
  .mobile-nav__cta,
  .menu-button span,
  .mobile-menu-enter-active,
  .mobile-menu-leave-active {
    transition: none;
  }
}
</style>
